import { useMemo, useState } from 'react';
import { slugify } from '../slugify';
import { ICON_NAMES } from '../../components/services/editable/commonIcon';
import {
  BLOG_CANONICAL_SLUG_BY_ID,
  resolveBlogCanonicalSlug,
  slugifyForUrl,
} from '../../seo/urlConfig';
import {
  blogTopicLink,
  countPostsInTopic,
  normalizeBlogCategories,
  syncBrowseTopicKeys,
} from '../../content/blogCategories';
import AdminModal from '../components/AdminModal';
import ConfirmDialog from '../components/ConfirmDialog';
import AdminPagination from '../components/AdminPagination';
import AdminFilterTabs from '../components/AdminFilterTabs';
import AdminCollapsible from '../components/AdminCollapsible';
import AdminSteps from '../components/AdminSteps';
import AdminDetailPrompt from '../components/AdminDetailPrompt';
import { usePagination } from '../hooks/usePagination';
import { useAdminPersist } from '../AdminPersistContext';
import { useEditorFeedback } from '../hooks/useEditorFeedback';
import { useNotifyFormDirty } from '../hooks/useNotifyFormDirty';

function isBlogArticleReady(post) {
  const content = (post?.content || '').trim();
  return content.length > 80 && !content.startsWith('## Your content here');
}

const PAGE_SIZE = 8;

const ICON_OPTIONS = ICON_NAMES.map((name) => ({ value: name, label: name }));

const emptyCategory = () => ({
  key: `topic-${Date.now()}`,
  name: 'New Category',
  iconUrl: '',
  icon: 'layers',
});

const emptyPost = (defaultTopicKey, categoryName) => ({
  id: Date.now(),
  tag: 'Enterprise AI',
  topicKey: defaultTopicKey,
  category: categoryName || 'AI & Automation',
  title: 'New blog post',
  slug: '',
  date: new Date().toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' }),
  readTime: '5 Min Read',
  image: '',
  coverImage: '',
  detailMedia: [],
  excerpt: '',
  content: '## Your content here\n\nWrite markdown-style sections.\n\nYou can place an image in the body:\n![Optional caption](https://res.cloudinary.com/.../image.jpg)',
});

export default function BlogsEditor({ content, onChange }) {
  const [filterKey, setFilterKey] = useState('all');
  const [editId, setEditId] = useState(null);
  const [isNewPost, setIsNewPost] = useState(false);
  const [categoryModal, setCategoryModal] = useState(null);
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [blogModalTab, setBlogModalTab] = useState('card');
  const [cardSavedPrompt, setCardSavedPrompt] = useState(false);
  const { persistNow } = useAdminPersist();
  const feedback = useEditorFeedback({ persistNow });

  const categories = useMemo(
    () => normalizeBlogCategories(content.blogs?.categories),
    [content.blogs?.categories]
  );
  const posts = content.blogs?.posts ?? [];

  const patchBlogs = (patch) => {
    const nextCategories = patch.categories ?? categories;
    onChange({
      ...content,
      blogs: {
        ...content.blogs,
        posts,
        ...patch,
        categories: nextCategories,
        browseTopicKeys: syncBrowseTopicKeys(nextCategories),
      },
    });
  };

  const updatePosts = (next) => patchBlogs({ posts: next });
  const updateCategories = (next) => patchBlogs({ categories: normalizeBlogCategories(next) });

  const filteredPosts = useMemo(() => {
    if (filterKey === 'all') return posts;
    return posts.filter((p) => p.topicKey === filterKey);
  }, [posts, filterKey]);

  const { page, setPage, totalPages, paginatedItems, totalItems } = usePagination(
    filteredPosts,
    PAGE_SIZE
  );

  const filterTabs = useMemo(
    () => [
      { key: 'all', label: 'All', count: posts.length },
      ...categories.map((cat) => ({
        key: cat.key,
        label: cat.name,
        count: countPostsInTopic(posts, cat.key),
      })),
    ],
    [posts, categories]
  );

  const editing = editId !== null ? posts.find((p) => String(p.id) === String(editId)) : null;
  const editingCategory =
    categoryModal && categoryModal !== 'add'
      ? categories.find((c) => c.key === categoryModal)
      : categoryModal === 'add'
        ? emptyCategory()
        : null;

  const openAddPost = () => {
    const cat = categories.find((c) => c.key === filterKey) || categories[0];
    if (!cat) return;
    const post = emptyPost(cat.key, cat.name);
    updatePosts([post, ...posts]);
    setEditId(post.id);
    setIsNewPost(true);
    setBlogModalTab('card');
    setCardSavedPrompt(false);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'post') {
      updatePosts(posts.filter((x) => String(x.id) !== String(deleteTarget.id)));
      if (String(editId) === String(deleteTarget.id)) setEditId(null);
    } else if (deleteTarget.type === 'category') {
      updateCategories(categories.filter((c) => c.key !== deleteTarget.id));
      if (filterKey === deleteTarget.id) setFilterKey('all');
      setCategoryModal(null);
    }
    setDeleteTarget(null);
  };

  const discardPostModal = () => {
    if (isNewPost && editId !== null) {
      updatePosts(posts.filter((p) => String(p.id) !== String(editId)));
    }
    setEditId(null);
    setIsNewPost(false);
    setCardSavedPrompt(false);
    feedback.setFormDirty(false);
  };

  const closePostModal = () => {
    feedback.requestClose({ isNew: isNewPost, onDiscard: discardPostModal });
  };

  const closeCategoryModal = () => {
    feedback.requestClose({
      isNew: categoryModal === 'add',
      onDiscard: () => {
        setCategoryModal(null);
        feedback.setFormDirty(false);
      },
    });
  };

  return (
    <div className="admin-panel">
      <div className="admin-panel__head">
        <div>
          <h2>Blog</h2>
          <p className="admin-panel__desc">
            Step 1: post <strong>card</strong> on the blog listing. Step 2: full <strong>article page</strong> when
            visitors open the post.
          </p>
        </div>
        <button
          type="button"
          className="admin-btn admin-btn--primary"
          onClick={openAddPost}
          disabled={!categories.length}
        >
          Add blog post
        </button>
      </div>

      <AdminSteps />

      {!categories.length ? (
        <p className="admin-error">Add a category first (button on the right).</p>
      ) : null}

      <AdminFilterTabs
        label="Topic"
        tabs={filterTabs}
        activeKey={filterKey}
        onChange={(key) => {
          setFilterKey(key);
          setPage(1);
        }}
        extra={
          <button
            type="button"
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={() => setManageCategoriesOpen(true)}
          >
            Topics
          </button>
        }
      />

      <div className="admin-toolbar">
        <span className="admin-toolbar__meta">{totalItems} post(s)</span>
      </div>

      <div className="admin-list">
        {paginatedItems.map((p) => (
          <div key={p.id} className="admin-list-item">
            <div>
              <strong>{p.title}</strong>
              {isBlogArticleReady(p) ? (
                <span className="admin-badge admin-badge--ok">Article ready</span>
              ) : (
                <span className="admin-badge admin-badge--warn">Needs article body</span>
              )}
              <br />
              <span>
                ID {p.id} · {categories.find((c) => c.key === p.topicKey)?.name || p.category || 'No category'}
              </span>
            </div>
            <div className="admin-list-actions">
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={() => {
                  setEditId(p.id);
                  setIsNewPost(false);
                  setBlogModalTab('card');
                  setCardSavedPrompt(false);
                }}
              >
                Edit card
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={() => {
                  setEditId(p.id);
                  setIsNewPost(false);
                  setBlogModalTab('article');
                  setCardSavedPrompt(false);
                }}
              >
                Article page
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--danger admin-btn--sm"
                onClick={() => setDeleteTarget({ type: 'post', id: p.id })}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        {!paginatedItems.length ? (
          <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>No posts in this category.</p>
        ) : null}
      </div>

      <AdminPagination
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      <AdminCollapsible title="Optional: blog videos" hint="Hero + card background videos">
        <div className="admin-field">
          <label>Hero video URL</label>
          <input
            value={content.blogs?.heroVideo || ''}
            onChange={(e) => {
              const heroVideo = e.target.value;
              onChange({
                ...content,
                blogs: { ...content.blogs, heroVideo, browseTopicKeys: syncBrowseTopicKeys(categories) },
                media: { ...content.media, blogHeroVideo: heroVideo },
              });
            }}
          />
        </div>
        <div className="admin-field">
          <label>Card videos (one URL per line)</label>
          <textarea
            rows={5}
            value={(content.blogs?.videoPool || []).join('\n')}
            onChange={(e) => {
              const videoPool = e.target.value
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean);
              onChange({
                ...content,
                blogs: { ...content.blogs, videoPool, browseTopicKeys: syncBrowseTopicKeys(categories) },
                media: { ...content.media, blogVideoPool: videoPool },
              });
            }}
          />
        </div>
      </AdminCollapsible>

      <AdminModal
        open={manageCategoriesOpen}
        onClose={() => {
          setManageCategoriesOpen(false);
          setCategoryModal(null);
        }}
        title="Manage blog categories"
        size="md"
      >
        <div className="admin-list" style={{ marginBottom: 16 }}>
          {categories.map((cat) => (
            <div key={cat.key} className="admin-list-item">
              <div>
                {cat.iconUrl ? (
                  <img src={cat.iconUrl} alt="" className="admin-preview-img admin-preview-img--square" />
                ) : null}
                <strong>{cat.name}</strong>
                <br />
                <span>
                  {blogTopicLink(cat.key)} · {countPostsInTopic(posts, cat.key)} article(s)
                </span>
              </div>
              <div className="admin-list-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn--ghost admin-btn--sm"
                  onClick={() => {
                    setManageCategoriesOpen(false);
                    setCategoryModal(cat.key);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn--danger admin-btn--sm"
                  onClick={() => setDeleteTarget({ type: 'category', id: cat.key, name: cat.name })}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="admin-btn admin-btn--primary"
          onClick={() => {
            setManageCategoriesOpen(false);
            setCategoryModal('add');
          }}
        >
          Add category
        </button>
      </AdminModal>

      <AdminModal
        open={!!categoryModal}
        onClose={closeCategoryModal}
        title={categoryModal === 'add' ? 'Add category' : 'Edit category'}
        size="md"
      >
        {editingCategory ? (
          <CategoryForm
            category={editingCategory}
            categories={categories}
            onSave={(updated) => {
              const oldKey = categoryModal === 'add' ? null : categoryModal;
              let next;
              if (categoryModal === 'add') {
                next = [...categories, updated];
              } else {
                next = categories.map((c) => (c.key === oldKey ? updated : c));
              }
              const nextPosts =
                oldKey && oldKey !== updated.key
                  ? posts.map((p) =>
                      p.topicKey === oldKey ? { ...p, topicKey: updated.key, category: updated.name } : p
                    )
                  : posts.map((p) =>
                      p.topicKey === updated.key ? { ...p, category: updated.name } : p
                    );
              patchBlogs({ categories: next, posts: nextPosts });
              setCategoryModal(null);
              feedback.notifySaved('Blog category saved to the server.');
            }}
            onCancel={closeCategoryModal}
            onDirtyChange={feedback.setFormDirty}
          />
        ) : null}
      </AdminModal>

      <AdminModal
        open={editId !== null}
        onClose={closePostModal}
        title={isNewPost ? 'Add blog post' : `Edit: ${editing?.title || ''}`}
        size="lg"
      >
        {editing ? (
          <>
            <div className="admin-modal-tabs">
              <button
                type="button"
                className={
                  blogModalTab === 'card' ? 'admin-modal-tab admin-modal-tab--active' : 'admin-modal-tab'
                }
                onClick={() => setBlogModalTab('card')}
              >
                1. Listing card
              </button>
              <button
                type="button"
                className={
                  blogModalTab === 'article' ? 'admin-modal-tab admin-modal-tab--active' : 'admin-modal-tab'
                }
                onClick={() => setBlogModalTab('article')}
              >
                2. Article page
              </button>
            </div>
            {cardSavedPrompt && blogModalTab === 'card' ? (
              <AdminDetailPrompt
                title="Listing card saved"
                message={`"${editing.title}" shows on the blog grid. Next, write the full article visitors read on the detail page.`}
                buttonLabel="Open article editor"
                onOpenDetail={() => {
                  setBlogModalTab('article');
                  setCardSavedPrompt(false);
                }}
              />
            ) : null}
            <BlogForm
              key={String(editId)}
              post={editing}
              tab={blogModalTab}
              categories={categories}
              existingIds={posts.map((p) => p.id).filter((id) => String(id) !== String(editId))}
              onSave={(updated) => {
                const cat = categories.find((c) => c.key === updated.topicKey);
                const withCategory = {
                  ...updated,
                  category: cat?.name || updated.category,
                };
                updatePosts(
                  posts.map((p) => (String(p.id) === String(editId) ? withCategory : p))
                );
                setEditId(withCategory.id);
                setIsNewPost(false);
                if (blogModalTab === 'card') setCardSavedPrompt(true);
                feedback.notifySaved(
                  blogModalTab === 'card'
                    ? 'Blog listing card saved to the server.'
                    : 'Blog article saved to the server.'
                );
              }}
              onCancel={closePostModal}
              onDirtyChange={feedback.setFormDirty}
              onGoArticle={() => setBlogModalTab('article')}
            />
          </>
        ) : null}
      </AdminModal>

      <ConfirmDialog
        open={!!deleteTarget}
        title={deleteTarget?.type === 'category' ? 'Remove category' : 'Delete post'}
        message={
          deleteTarget?.type === 'category'
            ? `Remove category "${deleteTarget.name}"? Posts keep their topic until you reassign.`
            : 'Delete this blog post?'
        }
        confirmLabel="Remove"
        danger
        onConfirm={handleDelete}
        cancelLabel="Cancel"
        onCancel={() => setDeleteTarget(null)}
      />

      {feedback.dialogs}
    </div>
  );
}

function CategoryForm({ category, categories, onSave, onCancel, onDirtyChange }) {
  const [form, setForm] = useState({ ...category });
  const [error, setError] = useState('');
  useNotifyFormDirty(form, category, onDirtyChange);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const key = slugify(form.key || form.name);
        if (!key) {
          setError('Category key is required.');
          return;
        }
        if (categories.some((c) => c.key === key && c.key !== category.key)) {
          setError('Another category already uses this key.');
          return;
        }
        onSave({
          ...form,
          key,
          name: form.name.trim() || key,
          icon: form.icon || 'layers',
        });
      }}
    >
      {error ? <p className="admin-error">{error}</p> : null}
      <div className="admin-field">
        <label>Display name</label>
        <input value={form.name} onChange={(e) => set('name', e.target.value)} required />
      </div>
      <div className="admin-grid-2">
        <div className="admin-field">
          <label>Topic key (URL)</label>
          <input value={form.key} onChange={(e) => set('key', e.target.value)} placeholder="ai-automation" />
          <span className="admin-field-hint">{blogTopicLink(slugify(form.key || form.name) || 'topic')}</span>
        </div>
        <div className="admin-field">
          <label>Fallback icon</label>
          <select value={form.icon} onChange={(e) => set('icon', e.target.value)}>
            {ICON_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="admin-field">
        <label>Icon SVG / image URL (optional)</label>
        <input
          value={form.iconUrl || ''}
          onChange={(e) => set('iconUrl', e.target.value)}
          placeholder="https://res.cloudinary.com/.../icon.svg"
        />
      </div>
      <div className="admin-form-actions">
        <button type="button" className="admin-btn admin-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="admin-btn admin-btn--primary">
          Save category
        </button>
      </div>
    </form>
  );
}

function BlogForm({ post, tab, categories, existingIds = [], onSave, onCancel, onGoArticle, onDirtyChange }) {
  const [form, setForm] = useState({ ...post });
  const [error, setError] = useState('');
  useNotifyFormDirty(form, post, onDirtyChange);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTopicChange = (topicKey) => {
    const cat = categories.find((c) => c.key === topicKey);
    setForm((f) => ({
      ...f,
      topicKey,
      category: cat?.name || f.category,
    }));
  };

  const onTitleChange = (title) => {
    setForm((f) => {
      const prevAuto = slugify(f.title || '');
      const slugUntouched = !f.slug || f.slug === prevAuto;
      return {
        ...f,
        title,
        slug: slugUntouched ? slugify(title) : f.slug,
      };
    });
  };

  const previewSrc = (form.coverImage || form.image || '').trim();
  const liveSlug = resolveBlogCanonicalSlug(form) || slugifyForUrl(form.slug || form.title) || 'your-post-title';
  const hasSeoCanonical = Boolean(BLOG_CANONICAL_SLUG_BY_ID[String(form.id)]);
  const savedSlugOutdated =
    hasSeoCanonical && form.slug && slugifyForUrl(form.slug) !== liveSlug;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (tab === 'card' && existingIds.some((id) => String(id) === String(form.id))) {
          setError(`Another post already uses ID ${form.id}.`);
          return;
        }
        if (tab === 'article' && !(form.content || '').trim()) {
          setError('Article body is required for the detail page.');
          return;
        }
        setError('');
        const detailMedia = (Array.isArray(form.detailMedia) ? form.detailMedia : [])
          .map((item) => ({
            afterNthSection: Math.max(1, Number(item.afterNthSection) || 1),
            src: String(item.src || '').trim(),
            alt: String(item.alt || '').trim(),
          }))
          .filter((item) => item.src);
        onSave({
          ...form,
          slug: liveSlug || slugifyForUrl(form.slug || form.title) || `post-${form.id}`,
          coverImage: (form.coverImage || '').trim(),
          image: (form.image || form.coverImage || '').trim(),
          detailMedia,
        });
      }}
    >
      {error ? <p className="admin-error">{error}</p> : null}

      {tab === 'card' ? (
        <>
          <p className="admin-panel__desc" style={{ marginTop: 0 }}>
            Card on the blog listing — title, unique cover, excerpt, tag, and topic (same layout as live site
            cards).
          </p>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label>Numeric ID (unique)</label>
              <input type="number" value={form.id} onChange={(e) => set('id', Number(e.target.value))} />
            </div>
            <div className="admin-field">
              <label>Read time</label>
              <input value={form.readTime} onChange={(e) => set('readTime', e.target.value)} />
            </div>
          </div>
          <div className="admin-field">
            <label>Title (card headline)</label>
            <input value={form.title} onChange={(e) => onTitleChange(e.target.value)} required />
          </div>
          <div className="admin-field">
            <label>SEO URL slug</label>
            <input
              value={form.slug || ''}
              onChange={(e) => set('slug', e.target.value)}
              placeholder={liveSlug || 'ai-integration-services-for-enterprises'}
            />
            <span className="admin-field-hint">Live URL: /blog/{liveSlug}</span>
            {savedSlugOutdated ? (
              <span className="admin-field-hint">
                The saved slug is outdated. The live site uses the URL above. Click Save to sync the CMS.
              </span>
            ) : null}
          </div>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label>Tag (pill on card)</label>
              <input value={form.tag} onChange={(e) => set('tag', e.target.value)} />
            </div>
            <div className="admin-field">
              <label>Topic / category</label>
              <select
                value={form.topicKey || categories[0]?.key || ''}
                onChange={(e) => onTopicChange(e.target.value)}
                required
              >
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="admin-field">
            <label>Date shown on card</label>
            <input value={form.date} onChange={(e) => set('date', e.target.value)} />
          </div>
          <div className="admin-field">
            <label>Unique cover image URL (recommended)</label>
            <input
              value={form.coverImage || ''}
              onChange={(e) => set('coverImage', e.target.value)}
              placeholder="https://res.cloudinary.com/.../your-unique-cover.jpg"
            />
            <span className="admin-field-hint">
              Use a different JPG/PNG/WebP per post so articles do not look identical. Paste a Cloudinary or CDN
              image URL.
            </span>
          </div>
          <div className="admin-field">
            <label>Optional video cover URL</label>
            <input
              value={form.image || ''}
              onChange={(e) => set('image', e.target.value)}
              placeholder="https://res.cloudinary.com/.../cover.mp4"
            />
            <span className="admin-field-hint">
              Leave empty to auto-assign a unique stock clip. If both are set, the cover image is preferred on
              the article page.
            </span>
          </div>
          {previewSrc ? (
            <div className="admin-field">
              <label>Cover preview</label>
              {/\.(mp4|webm|mov)(\?|$)/i.test(previewSrc) || previewSrc.includes('/video/') ? (
                <video
                  src={previewSrc}
                  className="admin-preview-img"
                  muted
                  playsInline
                  autoPlay
                  loop
                  style={{ maxHeight: 160, width: '100%', objectFit: 'cover', borderRadius: 12 }}
                />
              ) : (
                <img
                  src={previewSrc}
                  alt=""
                  className="admin-preview-img"
                  style={{ maxHeight: 160, width: '100%', objectFit: 'cover', borderRadius: 12 }}
                />
              )}
            </div>
          ) : null}
          <div className="admin-field">
            <label>Excerpt (short text on card)</label>
            <textarea value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} rows={3} />
          </div>
        </>
      ) : (
        <>
          <p className="admin-panel__desc" style={{ marginTop: 0 }}>
            Full <strong>article page</strong> — markdown sections (## headings) match the blog detail layout on
            the main site.
          </p>
          <div className="admin-field">
            <label>Article title (usually same as card)</label>
            <input value={form.title} onChange={(e) => set('title', e.target.value)} />
          </div>
          <div className="admin-field">
            <label>Article body (markdown)</label>
            <textarea
              value={form.content}
              onChange={(e) => set('content', e.target.value)}
              rows={16}
              placeholder={
                '## Introduction\n\nYour opening paragraph.\n\n![Desk setup for AI ops](https://res.cloudinary.com/.../unique.jpg)\n\n## Key takeaway\n\nMore content…'
              }
            />
            <span className="admin-field-hint">
              Use ## for headings. For in-article images paste a full line:{' '}
              <code>![Caption](https://…/unique-image.jpg)</code> — each blog should use its own URLs.
            </span>
          </div>

          <AdminCollapsible
            title="Inline images by section (optional)"
            hint="Shown after heading #N — use unique URLs per blog, not shared stock photos"
          >
            <p className="admin-panel__desc" style={{ marginTop: 0 }}>
              Example: after section <strong>2</strong> means after the 2nd <code>##</code> heading in the
              article. Prefer topic-related Cloudinary images.
            </p>
            {(Array.isArray(form.detailMedia) ? form.detailMedia : []).map((item, i) => (
              <div
                key={`dm-${i}`}
                className="admin-grid-2"
                style={{
                  marginBottom: 12,
                  paddingBottom: 12,
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                <div className="admin-field">
                  <label>Image URL #{i + 1}</label>
                  <input
                    value={item.src || ''}
                    onChange={(e) => {
                      const next = [...(form.detailMedia || [])];
                      next[i] = { ...next[i], src: e.target.value };
                      set('detailMedia', next);
                    }}
                    placeholder="https://res.cloudinary.com/.../blog-unique.jpg"
                  />
                </div>
                <div className="admin-field">
                  <label>After section #</label>
                  <input
                    type="number"
                    min={1}
                    value={item.afterNthSection || 1}
                    onChange={(e) => {
                      const next = [...(form.detailMedia || [])];
                      next[i] = { ...next[i], afterNthSection: Number(e.target.value) || 1 };
                      set('detailMedia', next);
                    }}
                  />
                </div>
                <div className="admin-field" style={{ gridColumn: '1 / -1' }}>
                  <label>Alt / caption</label>
                  <input
                    value={item.alt || ''}
                    onChange={(e) => {
                      const next = [...(form.detailMedia || [])];
                      next[i] = { ...next[i], alt: e.target.value };
                      set('detailMedia', next);
                    }}
                    placeholder="Short description of the image"
                  />
                </div>
                {item.src ? (
                  <div className="admin-field" style={{ gridColumn: '1 / -1' }}>
                    <img
                      src={item.src}
                      alt=""
                      className="admin-preview-img"
                      style={{ maxHeight: 120, width: '100%', objectFit: 'cover', borderRadius: 10 }}
                    />
                  </div>
                ) : null}
                <div className="admin-field" style={{ gridColumn: '1 / -1' }}>
                  <button
                    type="button"
                    className="admin-btn admin-btn--ghost"
                    onClick={() => {
                      const next = (form.detailMedia || []).filter((_, idx) => idx !== i);
                      set('detailMedia', next);
                    }}
                  >
                    Remove image #{i + 1}
                  </button>
                </div>
              </div>
            ))}
            {(form.detailMedia || []).length < 4 ? (
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                onClick={() =>
                  set('detailMedia', [
                    ...(form.detailMedia || []),
                    { afterNthSection: (form.detailMedia || []).length + 1, src: '', alt: '' },
                  ])
                }
              >
                + Add inline image
              </button>
            ) : null}
          </AdminCollapsible>
        </>
      )}

      <div className="admin-form-actions">
        <button type="button" className="admin-btn admin-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        {tab === 'card' ? (
          <button type="button" className="admin-btn admin-btn--ghost" onClick={onGoArticle}>
            Skip to article page →
          </button>
        ) : null}
        <button type="submit" className="admin-btn admin-btn--primary">
          {tab === 'card' ? 'Save listing card' : 'Save article page'}
        </button>
      </div>
    </form>
  );
}
